import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import Dashboard from './pages/Dashboard';
import ProcessingPage from './pages/ProcessingPage';
import TalentDetails from './pages/TalentDetails';
import TalentShowcase from './pages/TalentShowcase';
import ProfileDemo from './pages/ProfileDemo';
import ApiBasedProfileTest from './pages/ApiBasedProfileTest';
import ArchitectureVisualizationPage from './pages/ArchitectureVisualization';
import Settings from './pages/Settings';
import OAuthCallback from './components/OAuthCallback';
import Logout from './pages/Logout';
import { isAuthenticatedAtom } from './store/auth';
import { redirectToHostedUI } from './services/cognitoAuth';


function App() {
  const [isAuthenticated] = useAtom(isAuthenticatedAtom);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  // Check if we're on the callback or logout route
  // IMPORTANT: Check this BEFORE any redirects
  const isCallbackRoute = window.location.pathname === '/callback';
  const isLogoutRoute = window.location.pathname === '/logout';

  console.log('App render - pathname:', window.location.pathname, 'isLogoutRoute:', isLogoutRoute, 'isAuthenticated:', isAuthenticated);

  // On mount, verify localStorage is clean and matches auth state
  useEffect(() => {
    const checkAuthState = () => {
      const hasTokens = localStorage.getItem('id_token') && localStorage.getItem('access_token');
      const hasAuthState = localStorage.getItem('talrev_auth');

      // If we have no tokens but still have auth state, clear it (logout cleanup)
      if (!hasTokens && hasAuthState) {
        console.log('Cleaning up stale auth state after logout');
        localStorage.removeItem('talrev_auth');
      }

      setHasCheckedAuth(true);
    };

    checkAuthState();
  }, []);

  // If not authenticated and not on callback/logout route, redirect to Hosted UI
  useEffect(() => {
    if (hasCheckedAuth && !isAuthenticated && !isCallbackRoute && !isLogoutRoute) {
      // Add a small delay to prevent redirect loop during initialization
      const timer = setTimeout(() => {
        if (!isAuthenticated && !isCallbackRoute && !isLogoutRoute) {
          redirectToHostedUI();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isCallbackRoute, isLogoutRoute, hasCheckedAuth]);

  // Show loading while checking auth status
  if (!isAuthenticated && !isCallbackRoute && !isLogoutRoute) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <main>
          <Routes>
            <Route path="/callback" element={<OAuthCallback />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/" element={<Dashboard />} />
            <Route path="/processing/:id" element={<ProcessingPage />} />
            <Route path="/talents/:id" element={<TalentDetails />} />
            <Route path="/talents" element={<TalentShowcase />} />
            <Route path="/profile-demo" element={<ProfileDemo />} />
            <Route path="/api-profile-test" element={<ApiBasedProfileTest />} />
            <Route path="/about" element={<ArchitectureVisualizationPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/team" element={<TeamView />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

// Placeholder components for missing pages
const TeamView: React.FC = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <h1 className="text-[var(--font-size-4xl)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] text-[var(--text-primary)] mb-4">Team View</h1>
    <p className="text-[var(--text-tertiary)] mt-2">Compare talents across your team.</p>
    <div className="mt-8 bg-white p-8 rounded-lg border-2 border-dashed border-gray-300">
      <p className="text-center text-[var(--text-tertiary)]">Team comparison view coming soon...</p>
    </div>
  </div>
);

const NotFound: React.FC = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="text-center">
      <h1 className="text-[var(--font-size-3xl)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] text-[var(--text-primary)] mb-4">404 - Page Not Found</h1>
      <p className="text-[var(--text-tertiary)] mt-2">Page not found</p>
    </div>
  </div>
);

export default App;
